/**
 * Cash Casino — every sound, synthesised. Same skeleton as
 * andar_bahar/sound.ts and teen_patti/sound.ts (lazy AudioContext,
 * tone()/noise() primitives, a bed bus the duck covers, a lookahead
 * scheduler pump) with the `Cs` prefix.
 *
 * Bed: "Neon Floor" — 118 BPM, F major, upbeat lounge-electro: a plucked
 * square bass (F–F–C–D over four bars), triangle chord stabs on the
 * off-beats, a noise hi-hat on 8ths (16ths while the reels run) and a soft
 * pitched-sine kick on 1 and 3.
 */

const LS_SOUND = 'cs_sound';
const LS_MUSIC = 'cs_music';
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let soundOn = true, musicOn = true;
let _duckedUntil = 0;

const lsGet = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const lsSet = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* private */ } };
export function initCsSound(): boolean { soundOn = lsGet(LS_SOUND) !== '0'; return soundOn; }
export function isCsSoundOn(): boolean { return soundOn; }
export function setCsSoundOn(v: boolean): boolean { soundOn = v; lsSet(LS_SOUND, v ? '1' : '0'); if (!v) stopCsMusic(); else if (musicOn) startCsMusic(); return v; }
export function initCsMusic(): boolean { musicOn = lsGet(LS_MUSIC) !== '0'; return musicOn; }
export function isCsMusicOn(): boolean { return musicOn; }
export function setCsMusicOn(v: boolean): boolean { musicOn = v; lsSet(LS_MUSIC, v ? '1' : '0'); if (v && soundOn) startCsMusic(); else stopCsMusic(); return v; }

function ac(): AudioContext | null {
  if (ctx) return ctx;
  try {
    const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    ctx = new AC(); master = ctx.createGain(); master.gain.value = 1; master.connect(ctx.destination);
  } catch { ctx = null; }
  return ctx;
}
export function unlockCsSound(): void { const c = ac(); if (c && c.state === 'suspended' && !document.hidden) void c.resume(); }

function bus(gain: number): { c: AudioContext; g: GainNode } | null {
  if (!soundOn) return null;
  const c = ac(); if (!c || !master) return null;
  const g = c.createGain(); g.gain.value = gain; g.connect(master);
  return { c, g };
}
function tone(freq: number, dur: number, o: { type?: OscillatorType; to?: number; gain?: number; delay?: number } = {}) {
  const b = bus(o.gain ?? 0.08); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const osc = b.c.createOscillator(); osc.type = o.type ?? 'sine';
  osc.frequency.setValueAtTime(freq, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
  b.g.gain.setValueAtTime(0.0001, t0);
  b.g.gain.exponentialRampToValueAtTime(o.gain ?? 0.08, t0 + 0.006);
  b.g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(b.g); osc.start(t0); osc.stop(t0 + dur + 0.05);
}
function noise(dur: number, o: { f0?: number; f1?: number; q?: number; gain?: number; delay?: number } = {}) {
  const b = bus(o.gain ?? 0.06); if (!b) return;
  const t0 = b.c.currentTime + (o.delay ?? 0);
  const n = Math.floor(b.c.sampleRate * dur);
  const buf = b.c.createBuffer(1, n, b.c.sampleRate); const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = b.c.createBufferSource(); src.buffer = buf;
  const f = b.c.createBiquadFilter(); f.type = 'bandpass';
  f.frequency.setValueAtTime(o.f0 ?? 1200, t0);
  if (o.f1) f.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur);
  f.Q.value = o.q ?? 1;
  src.connect(f); f.connect(b.g); src.start(t0);
}
/** brass-like sawtooth stack through an opening lowpass — the fanfare voice */
function brass(freqs: readonly number[], delay: number, dur: number, peak: number) {
  if (!soundOn) return;
  const c = ac(); if (!c || !master) return;
  const at = c.currentTime + delay;
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.Q.value = 0.9;
  lp.frequency.setValueAtTime(480, at);
  lp.frequency.exponentialRampToValueAtTime(4600, at + Math.min(0.22, dur * 0.45));
  lp.frequency.exponentialRampToValueAtTime(1100, at + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.045);
  g.gain.setValueAtTime(peak, at + dur * 0.62);
  g.gain.exponentialRampToValueAtTime(0.0001, at + dur);
  lp.connect(g); g.connect(master);
  for (const f of freqs) for (const det of [-7, 7]) {
    const o = c.createOscillator(); o.type = 'sawtooth'; o.frequency.value = f; o.detune.value = det;
    o.connect(lp); o.start(at); o.stop(at + dur + 0.06);
  }
}

/* pitch table — F major */
const F1 = 43.65, F2 = 87.31, A2 = 110, Bb2 = 116.54, C3 = 130.81, D3 = 146.83,
  F3 = 174.61, G3 = 196, A3 = 220, Bb3 = 233.08, C4 = 261.63, D4 = 293.66, E4 = 329.63,
  F4 = 349.23, G4 = 392, A4 = 440, Bb4 = 466.16, C5 = 523.25, D5 = 587.33, E5 = 659.26,
  F5 = 698.46, G5 = 783.99, A5 = 880, Bb5 = 932.33, C6 = 1046.5, D6 = 1174.66, E6 = 1318.51,
  F6 = 1396.91, G6 = 1567.98, A6 = 1760, C7 = 2093;
void A2; void Bb2; void Bb5; void E6;

/* ─── one-shots ───────────────────────────────────────────────────────── */

/** the pull — a mechanical clunk, then a whir that rises for ~300 ms */
export function sSpinStart(): void {
  tone(140, 0.10, { type: 'sine', to: 58, gain: 0.17 });
  noise(0.06, { f0: 520, f1: 180, q: 0.8, gain: 0.11 });
  noise(0.022, { f0: 2600, f1: 1700, q: 2.4, gain: 0.05 });
  noise(0.27, { f0: 380, f1: 2100, q: 1.5, gain: 0.045, delay: 0.045 });
  tone(96, 0.28, { type: 'sawtooth', to: 300, gain: 0.035, delay: 0.045 });
  tone(192, 0.24, { type: 'triangle', to: 600, gain: 0.018, delay: 0.06 });
  duckCsMusic(280);
}

/** a symbol crossing the payline — capped at one click per 45 ms overall */
let lastTick = 0;
export function sReelTick(reel: number): void {
  const now = performance.now();
  if (now - lastTick < 45) return;
  lastTick = now;
  const r = Math.max(0, Math.min(2, Math.round(reel)));
  const k = 1 + r * 0.09;
  const q = now < _duckedUntil ? 0.45 : 1;      /* stay out of a fanfare's way */
  noise(0.012, { f0: 2500 * k, f1: 1700 * k, q: 3.2, gain: 0.030 * q });
  tone(760 * k, 0.020, { type: 'square', to: 520 * k, gain: 0.016 * q });
}

/** a reel settling — solid thunk plus a short metallic ring, rising by index */
export function sReelStop(reel: number): void {
  const r = Math.max(0, Math.min(2, Math.round(reel)));
  const k = Math.pow(2, r * 2 / 12);
  tone(158 * k, 0.11, { type: 'sine', to: 72 * k, gain: 0.17 });
  noise(0.075, { f0: 460, f1: 160, q: 0.85, gain: 0.10 });
  noise(0.020, { f0: 3200 * k, f1: 2200 * k, q: 2.4, gain: 0.045 });
  tone(1180 * k, 0.16, { type: 'triangle', gain: 0.038, delay: 0.012 });
  tone(1770 * k, 0.11, { type: 'sine', gain: 0.016, delay: 0.012 });
  duckCsMusic(190);
}

/** two sevens up and the last reel still spinning — a tense two-note sting */
export function sNearMiss(): void {
  tone(Bb3, 0.20, { type: 'sawtooth', gain: 0.055 });
  tone(Bb3 * 1.006, 0.20, { type: 'triangle', gain: 0.035 });
  tone(E4, 0.34, { type: 'sawtooth', gain: 0.055, delay: 0.16 });
  tone(E4 * 0.994, 0.34, { type: 'triangle', gain: 0.032, delay: 0.16 });
  tone(F2, 0.40, { type: 'sine', gain: 0.075, delay: 0.16 });
  noise(0.30, { f0: 1400, f1: 3000, q: 1.4, gain: 0.016, delay: 0.16 });
  duckCsMusic(420);
}

/** winning line i (0..4); brightness scales with x (5..25) — 120 ms apart it runs up */
const LINE_NOTE = [F5, G5, A5, C6, D6];
export function sLineWin(i: number, x: number): void {
  const n = Math.max(0, Math.min(4, Math.round(i)));
  const b = Math.max(0, Math.min(1, (x - 5) / 20));
  const f = LINE_NOTE[n];
  tone(f, 0.24 + 0.10 * b, { type: 'triangle', gain: 0.085 + 0.045 * b });
  tone(f * 2, 0.16 + 0.10 * b, { type: 'sine', gain: 0.026 + 0.030 * b, delay: 0.008 });
  if (b > 0.35) tone(f * 3, 0.12, { type: 'sine', gain: 0.012 + 0.020 * b, delay: 0.014 });
  noise(0.09 + 0.14 * b, { f0: 4200 + 2400 * b, f1: 7000 + 3500 * b, q: 1.1, gain: 0.012 + 0.020 * b, delay: 0.02 });
  duckCsMusic(200 + Math.round(180 * b));
}

/** three keno balls anywhere — a sparkly three-note bell run */
export function sScatter(): void {
  [C6, F6, A6].forEach((f, i) => {
    tone(f, 0.34, { type: 'sine', gain: 0.10 - 0.015 * i, delay: i * 0.09 });
    tone(f * 2.01, 0.18, { type: 'sine', gain: 0.022, delay: i * 0.09 });
  });
  noise(0.55, { f0: 6200, f1: 11000, q: 0.8, gain: 0.040, delay: 0.10 });
  tone(C7, 0.60, { type: 'sine', gain: 0.030, delay: 0.24 });
  duckCsMusic(600);
}

/** one coin clink — the coin flight fires up to eight of these */
const COIN = [C6, E5, F6, G6, A6];
export function sCoin(delayMs = 0): void {
  const d = Math.max(0, delayMs) / 1000;
  const f = COIN[Math.floor(Math.random() * COIN.length)];
  tone(f, 0.085, { type: 'sine', gain: 0.070, delay: d });
  tone(f * 1.49, 0.055, { type: 'sine', gain: 0.026, delay: d + 0.004 });
  noise(0.018, { f0: 5200, f1: 3600, q: 3, gain: 0.028, delay: d });
}
function cascade(k: number, delay: number, gain: number) {
  const coins = [F6, A6, C7, G6, C6];
  for (let i = 0; i < k; i++) {
    tone(coins[i % 5], 0.12, { type: 'sine', gain: gain - gain * 0.5 * (i / Math.max(1, k - 1)), delay: delay + i * 0.052 });
    noise(0.016, { f0: 5400, f1: 3800, q: 3, gain: gain * 0.30, delay: delay + i * 0.052 });
  }
}

/** win — three tiers by amount: coin cascade over a chord */
export function sWin(amount: number): void {
  const big = amount >= 2000, mid = !big && amount >= 500;
  const chord = big ? [F5, A5, C6, F6] : mid ? [F5, C6, F6] : [F5, C6];
  chord.forEach((f, i) => tone(f, big ? 0.36 : 0.28, { type: 'triangle', gain: big ? 0.16 : 0.14, delay: i * 0.075 }));
  cascade(big ? 11 : mid ? 7 : 4, 0.30, 0.090);
  noise(big ? 0.75 : 0.45, { f0: 6000, f1: big ? 11000 : 9000, q: 0.7, gain: big ? 0.055 : 0.040, delay: 0.32 });
  if (mid || big) tone(F6, 0.70, { type: 'sine', gain: 0.048, delay: 0.48 });
  if (big) { tone(C7, 0.90, { type: 'sine', gain: 0.038, delay: 0.60 }); tone(F3, 0.38, { type: 'sine', to: F2, gain: 0.15, delay: 0.28 }); }
  duckCsMusic(big ? 900 : mid ? 550 : 350);
}

/** X ≥ 15 — a 1.6 s major fanfare */
export function sBigWin(): void {
  brass([F3, A3, C4, F4], 0.00, 0.34, 0.085);
  brass([Bb3, D4, F4], 0.32, 0.30, 0.085);
  brass([C4, E4, G4, C5], 0.62, 0.30, 0.090);
  brass([F4, A4, C5, F5], 0.94, 0.66, 0.100);
  tone(F2, 0.30, { type: 'sine', to: F1, gain: 0.16 });
  tone(F2, 0.34, { type: 'sine', to: F1, gain: 0.15, delay: 0.94 });
  noise(0.60, { f0: 5000, f1: 10500, q: 0.7, gain: 0.045, delay: 0.90 });
  cascade(7, 1.02, 0.070);
  tone(F6, 0.80, { type: 'sine', gain: 0.045, delay: 1.10 });
  duckCsMusic(1750);
}

/** three sevens — a 3 s fanfare under a coin waterfall */
export function sJackpot(): void {
  brass([F3, A3, C4, F4], 0.00, 0.30, 0.090);
  brass([F3, A3, C4, F4], 0.30, 0.22, 0.085);
  brass([Bb3, D4, F4, Bb4], 0.54, 0.34, 0.090);
  brass([C4, E4, G4, C5], 0.90, 0.34, 0.095);
  brass([D4, F4, A4, D5], 1.26, 0.30, 0.090);
  brass([C4, E4, G4, C5], 1.58, 0.30, 0.095);
  brass([F4, A4, C5, F5], 1.90, 1.05, 0.105);
  for (const d of [0, 0.54, 0.90, 1.90]) tone(F2, 0.32, { type: 'sine', to: F1, gain: 0.16, delay: d });
  cascade(18, 0.35, 0.080);
  cascade(12, 1.95, 0.075);
  noise(0.90, { f0: 5200, f1: 12000, q: 0.7, gain: 0.050, delay: 1.85 });
  tone(F6, 1.10, { type: 'sine', gain: 0.048, delay: 2.05 });
  tone(C7, 0.95, { type: 'sine', gain: 0.030, delay: 2.20 });
  duckCsMusic(3200);
}

/** nothing on the lines — a very quiet tick-tock and it is over */
export function sNoWin(): void {
  noise(0.014, { f0: 2400, f1: 1700, q: 3, gain: 0.014 });
  tone(A3, 0.05, { type: 'sine', gain: 0.022 });
  noise(0.014, { f0: 1900, f1: 1300, q: 3, gain: 0.012, delay: 0.14 });
  tone(F3, 0.08, { type: 'sine', gain: 0.020, delay: 0.14 });
}

/* ─── UI ──────────────────────────────────────────────────────────────── */
export const sChip = (): void => { tone(A5, 0.06, { type: 'sine', gain: 0.13 }); tone(C6, 0.07, { type: 'sine', gain: 0.10, delay: 0.05 }); noise(0.03, { f0: 3000, f1: 2200, q: 2, gain: 0.09 }); };
export const sTap = (): void => { tone(A5, 0.045, { type: 'sine', to: F5, gain: 0.05 }); };
export const sDenied = (): void => { tone(175, 0.1, { type: 'square', gain: 0.09 }); tone(175, 0.1, { type: 'square', gain: 0.09, delay: 0.14 }); };
export const sAutoOn = (): void => { tone(F5, 0.075, { type: 'triangle', gain: 0.085 }); tone(C6, 0.13, { type: 'triangle', gain: 0.080, delay: 0.075 }); };
export const sAutoOff = (): void => { tone(C6, 0.075, { type: 'triangle', gain: 0.075 }); tone(F5, 0.15, { type: 'triangle', gain: 0.070, delay: 0.075 }); };

/* ─── the bed: "Neon Floor" — 118 BPM, F major ─────────────────────────── */
const BPM = 118, BEAT = 60 / BPM, BAR = 4 * BEAT, STEP = BAR / 16, LOOP = 4 * BAR;
const M_MASTER = 0.34, DUCK_FLOOR = 0.30;
let I = 0.35, OPEN = 0.22, chordsOn = false, hats16 = false, lastPhase = '';
let musicLive = false, loopStart = 0, nextEv = 0, bedTimer = 0, killTimer = 0;
let bedGain: GainNode | null = null;
const live: { o: AudioScheduledSourceNode; end: number }[] = [];
/* bass root + stab voicing per bar of the four-bar loop: F – F – C – Dm */
const CHART: { root: number; stab: readonly number[] }[] = [
  { root: F2, stab: [F3, A3, C4] },
  { root: F2, stab: [F3, A3, C4] },
  { root: C3, stab: [G3, C4, E4] },
  { root: D3, stab: [F3, A3, D4] },
];

function bedTone(f: number, dur: number, o: { type?: OscillatorType; to?: number; gain?: number; delay?: number }) {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const osc = c.createOscillator(); osc.type = o.type ?? 'sine'; osc.frequency.setValueAtTime(f, t0);
  if (o.to) osc.frequency.exponentialRampToValueAtTime(o.to, t0 + dur);
  const g = c.createGain(); g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(o.gain ?? 0.05, t0 + 0.006);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g); g.connect(bedGain); osc.start(t0); osc.stop(t0 + dur + 0.05);
  live.push({ o: osc, end: t0 + dur + 0.05 });
}
function bedNoise(dur: number, o: { f0: number; f1: number; q: number; gain: number; delay?: number }) {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t0 = c.currentTime + (o.delay ?? 0);
  const n = Math.floor(c.sampleRate * dur); const buf = c.createBuffer(1, n, c.sampleRate); const d = buf.getChannelData(0);
  for (let i = 0; i < n; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / n);
  const src = c.createBufferSource(); src.buffer = buf;
  const f = c.createBiquadFilter(); f.type = 'bandpass';
  f.frequency.setValueAtTime(o.f0, t0); f.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur); f.Q.value = o.q;
  const g = c.createGain(); g.gain.value = o.gain;
  src.connect(f); f.connect(g); g.connect(bedGain); src.start(t0);
  live.push({ o: src, end: t0 + dur });
}
/** plucked bass: square through a resonant lowpass that OPEN steers */
function pluck(c: AudioContext, out: GainNode, f: number, at: number, peak: number, decay: number) {
  const cut = 300 + 2500 * OPEN;
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.Q.value = 5.5;
  lp.frequency.setValueAtTime(cut * 1.7, at);
  lp.frequency.exponentialRampToValueAtTime(Math.max(150, cut * 0.55), at + decay);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.008);
  g.gain.exponentialRampToValueAtTime(0.0001, at + decay);
  const o = c.createOscillator(); o.type = 'square'; o.frequency.value = f;
  const sub = c.createOscillator(); sub.type = 'sine'; sub.frequency.value = f / 2;
  const sg = c.createGain(); sg.gain.value = 0.45;
  o.connect(lp); sub.connect(sg); sg.connect(lp); lp.connect(g); g.connect(out);
  const end = at + decay + 0.05;
  o.start(at); o.stop(end); sub.start(at); sub.stop(end);
  live.push({ o, end }, { o: sub, end });
}
/** muted chord stab: a detuned triangle stack, short */
function stab(c: AudioContext, out: GainNode, freqs: readonly number[], at: number, peak: number, decay: number) {
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.Q.value = 1.0;
  lp.frequency.setValueAtTime(900 + 3200 * OPEN, at);
  lp.frequency.exponentialRampToValueAtTime(700 + 900 * OPEN, at + decay);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, at);
  g.gain.exponentialRampToValueAtTime(peak, at + 0.010);
  g.gain.exponentialRampToValueAtTime(0.0001, at + decay);
  lp.connect(g); g.connect(out);
  for (const f of freqs) for (const det of [-5, 5]) {
    const o = c.createOscillator(); o.type = 'triangle'; o.frequency.value = f; o.detune.value = det;
    o.connect(lp); o.start(at); o.stop(at + decay + 0.04); live.push({ o, end: at + decay + 0.04 });
  }
}
function pump() {
  const c = ctx; if (!c || !musicLive || !bedGain) return;
  const horizon = c.currentTime + 0.20;
  const earliest = c.currentTime - STEP;
  if (nextEv < earliest) nextEv = loopStart + Math.ceil((earliest - loopStart) / STEP) * STEP;
  while (nextEv < horizon) {
    const pos = (nextEv - loopStart) % LOOP;
    const bar = Math.floor(pos / BAR) % 4, s = Math.round((pos % BAR) / STEP) % 16;
    const d = Math.max(0, nextEv - c.currentTime), at = nextEv;
    const ch = CHART[bar];
    /* kick on 1 and 3 */
    if (s === 0 || s === 8) bedTone(120, 0.13, { type: 'sine', to: 46, gain: 0.075 + 0.030 * I, delay: d });
    /* hats: 8ths, doubled to 16ths while the reels run */
    const hg = 0.010 + 0.024 * I;
    if (hats16 || s % 2 === 0) {
      const off = s % 2 === 1;
      bedNoise(off ? 0.012 : 0.018, { f0: off ? 8600 : 7800, f1: off ? 6600 : 5800, q: 3, gain: off ? hg * 0.45 : hg * (s % 4 === 0 ? 1.25 : 1) , delay: d });
    }
    /* bass: quarters when sparse, 8ths with octave lifts when the loop is full */
    const bg = 0.075 * (0.55 + 0.45 * I);
    if (s % 4 === 0) pluck(c, bedGain, ch.root, at, bg, 0.24);
    else if (I >= 0.45 && s % 2 === 0) pluck(c, bedGain, s % 8 === 6 ? ch.root * 2 : ch.root, at, bg * 0.62, 0.17);
    /* chord stabs on the off-beats */
    if (chordsOn && (s === 6 || s === 14)) stab(c, bedGain, ch.stab, at, 0.030 + 0.028 * I, 0.20);
    if (chordsOn && I >= 0.78 && (s === 2 || s === 10)) stab(c, bedGain, ch.stab, at, 0.018 + 0.014 * I, 0.14);
    nextEv += STEP;
  }
  if (live.length > 160) { const now = c.currentTime; for (let i = live.length - 1; i >= 0; i--) if (live[i].end < now) live.splice(i, 1); }
}
/** win — a bright chord swells for ~1.2 s over the bed, then it is idle again */
function swell() {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t0 = c.currentTime;
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.Q.value = 1.1;
  lp.frequency.setValueAtTime(600, t0);
  lp.frequency.exponentialRampToValueAtTime(5200, t0 + 0.40);
  lp.frequency.exponentialRampToValueAtTime(800, t0 + 1.2);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.080, t0 + 0.30);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.2);
  lp.connect(g); g.connect(bedGain);
  for (const f of [F3, C4, F4, A4, C5]) {
    const o = c.createOscillator(); o.type = 'triangle'; o.frequency.value = f; o.detune.value = Math.random() * 8 - 4;
    o.connect(lp); o.start(t0); o.stop(t0 + 1.3); live.push({ o, end: t0 + 1.3 });
  }
}
/** bigwin — a sustained shimmer pad rides the loop for ~3 s, then decays */
function shimmer() {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t0 = c.currentTime;
  const lp = c.createBiquadFilter(); lp.type = 'lowpass'; lp.Q.value = 0.8;
  lp.frequency.setValueAtTime(900, t0);
  lp.frequency.exponentialRampToValueAtTime(6000, t0 + 1.1);
  lp.frequency.exponentialRampToValueAtTime(1400, t0 + 3.2);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.070, t0 + 0.55);
  g.gain.setValueAtTime(0.070, t0 + 2.3);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 3.2);
  lp.connect(g); g.connect(bedGain);
  const end = t0 + 3.3;
  for (const f of [F4, A4, C5, F5, A5, C6]) for (const det of [-6, 6]) {
    const o = c.createOscillator(); o.type = 'triangle'; o.frequency.value = f; o.detune.value = det;
    o.connect(lp); o.start(t0); o.stop(end); live.push({ o, end });
  }
}
export function csBedPhase(phase: 'idle' | 'spinning' | 'win' | 'bigwin', extra = 0): void {
  const e = Math.min(1, Math.max(0, extra)), fresh = phase !== lastPhase;
  lastPhase = phase;
  if (phase === 'spinning') { I = 0.55 + 0.35 * e; OPEN = 0.30 + 0.60 * e; chordsOn = true; hats16 = true; }
  else if (phase === 'win') { I = 0.40; OPEN = 0.45; chordsOn = false; hats16 = false; if (fresh) swell(); }
  else if (phase === 'bigwin') { I = 0.70; OPEN = 0.72; chordsOn = true; hats16 = false; if (fresh) shimmer(); }
  else { I = 0.35; OPEN = 0.22; chordsOn = false; hats16 = false; }
}

export function startCsMusic(): void {
  if (musicLive || !soundOn || !musicOn) return;
  const c = ac(); if (!c || !master) return;
  window.clearTimeout(killTimer); killTimer = 0;
  bedGain = c.createGain(); bedGain.gain.value = 0.0001; bedGain.connect(master);
  bedGain.gain.exponentialRampToValueAtTime(M_MASTER, c.currentTime + BAR);
  musicLive = true; loopStart = c.currentTime + 0.10; nextEv = loopStart;
  pump(); bedTimer = window.setInterval(pump, 50);
}
export function stopCsMusic(): void {
  musicLive = false; window.clearInterval(bedTimer); bedTimer = 0; _duckedUntil = 0; lastPhase = '';
  const c = ctx, g = bedGain, mine = live.splice(0); bedGain = null;
  if (!c || !g) return;
  const now = c.currentTime;
  g.gain.cancelScheduledValues(now); g.gain.setValueAtTime(Math.max(g.gain.value, 0.0001), now);
  g.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
  for (const nd of mine) { try { nd.o.stop(now + 0.18); } catch { /* done */ } }
  window.clearTimeout(killTimer);
  killTimer = window.setTimeout(() => { killTimer = 0; try { g.disconnect(); } catch { /* gone */ } }, 420);
}
export function duckCsMusic(ms: number): void {
  const c = ctx; if (!c || !bedGain || !musicLive) return;
  const t = c.currentTime;
  bedGain.gain.cancelScheduledValues(t); bedGain.gain.setValueAtTime(Math.max(bedGain.gain.value, 0.0001), t);
  bedGain.gain.exponentialRampToValueAtTime(M_MASTER * DUCK_FLOOR, t + 0.05);
  bedGain.gain.setValueAtTime(M_MASTER * DUCK_FLOOR, t + ms / 1000);
  bedGain.gain.exponentialRampToValueAtTime(M_MASTER, t + ms / 1000 + 0.5);
  _duckedUntil = performance.now() + ms + 500;
}
export function bindCsVisibility(): () => void {
  const h = () => { const c = ctx; if (!c) return; if (document.hidden) { void c.suspend(); } else if (soundOn) { void c.resume(); } };
  document.addEventListener('visibilitychange', h);
  return () => document.removeEventListener('visibilitychange', h);
}
